/**
 * Pure HTML rendering functions
 * Generate HTML strings without DOM manipulation
 *
 * New unified approach: Each row contains spans for ALL contexts (all/free/paid + categories)
 * CSS toggles visibility based on container's .view-{context} class
 */

import { escapeHtml, log } from "./core.js";
import { getColumnsForType } from "./columns.js";

const MEDAL_COLORS = {
  1: "#f4b400",
  2: "#c0c7d1",
  3: "#c97a33",
};

const DEFAULT_CREATOR_AVATAR_URL = "https://www.framer.com/creators/layout/default-avatar.png";

function normalizeCreatorAvatarUrl(value) {
  if (typeof value !== "string") return DEFAULT_CREATOR_AVATAR_URL;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_CREATOR_AVATAR_URL;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (trimmed.startsWith("/")) return `https://www.framer.com${trimmed}`;
  return DEFAULT_CREATOR_AVATAR_URL;
}

function getCategoryLabel(categoryId) {
  if (typeof window !== "undefined") {
    const metaName = window.__RANKS_CATEGORY_META__?.[categoryId]?.name;
    if (metaName) return metaName.replace(/\s*Templates?\s*$/i, "").trim();
  }

  const lastSegment = String(categoryId).split("/").filter(Boolean).pop() || String(categoryId);
  return lastSegment.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Normalize numeric trend values while preserving relative shape.
 * Null/undefined entries are preserved as gaps.
 * This enables obfuscating absolute ranks without changing sparkline geometry.
 *
 * @param {Array<number|null|undefined>} trendData
 * @param {{targetMin?: number, targetMax?: number}} options
 * @returns {Array<number|null>}
 */
export function normalizeTrendForObfuscation(trendData, options = {}) {
  if (!Array.isArray(trendData)) return [];

  const targetMin = Number.isFinite(options.targetMin) ? options.targetMin : 0;
  const targetMax = Number.isFinite(options.targetMax) ? options.targetMax : 100;
  const numericValues = trendData.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (numericValues.length === 0) {
    return trendData.map((v) => (v === null || v === undefined ? null : null));
  }

  const sourceMin = Math.min(...numericValues);
  const sourceMax = Math.max(...numericValues);
  const sourceRange = sourceMax - sourceMin;
  const targetRange = targetMax - targetMin;
  const midpoint = targetMin + targetRange / 2;

  return trendData.map((value) => {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    if (sourceRange === 0) return Number(midpoint.toFixed(4));
    const normalized = targetMin + ((num - sourceMin) / sourceRange) * targetRange;
    return Number(normalized.toFixed(4));
  });
}

function shouldRenderSparklinePointLabels() {
  if (typeof window === "undefined") return "none";
  const mode = window.__RANKS_SPARKLINE_LABEL_MODE__;
  return mode === "all" ? "all" : "none";
}

function shouldRenderSparklineTitleAttr() {
  if (typeof window === "undefined") return false;
  return window.__RANKS_SPARKLINE_TITLE_ATTR__ === true;
}

function renderMedalSvg(rank, size = 14, extraClass = "") {
  const classSuffix = extraClass ? ` ${extraClass}` : "";
  const medalRank = MEDAL_COLORS[rank] ? rank : 3;
  return `<span class="medal-svg medal-rank-${medalRank}${classSuffix}"><svg width="${size}" height="${size}" viewBox="0 0 491.768 491.768" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true"><g><path d="M174.048,136.543l39.925-83.12l18.234-37.966l-2.938-6.131C226.54,3.628,220.778,0,214.453,0H98.137 c-5.651,0-10.899,2.906-13.918,7.689c-3.001,4.783-3.338,10.772-0.898,15.859l72.508,150.957L174.048,136.543z"/><path d="M407.548,7.689C404.53,2.906,399.281,0,393.63,0h-116.3c-6.323,0-12.088,3.628-14.815,9.326l-89.814,187.012 c-0.065,0.145-0.081,0.289-0.146,0.433c-0.143,0.322-0.209,0.675-0.337,1.013c-0.577,1.559-0.913,3.211-0.994,4.959 c-0.017,0.242-0.145,0.45-0.145,0.709v8.474c22.329-11.687,47.676-18.397,74.579-18.397c26.922,0,52.252,6.711,74.597,18.414v-4.751 l88.192-183.642C410.887,18.461,410.549,12.473,407.548,7.689z"/><path d="M245.658,218.186c-75.559,0-136.8,61.24-136.8,136.799c0,75.543,61.241,136.783,136.8,136.783 c75.543,0,136.785-61.24,136.785-136.783C382.444,279.426,321.201,218.186,245.658,218.186z M326.966,331.1l-36.52,35.62 c-1.14,1.122-1.671,2.729-1.397,4.302l8.622,50.293c0.303,1.813-0.452,3.659-1.944,4.75c-1.492,1.092-3.483,1.221-5.105,0.369 l-45.17-23.738c-1.412-0.742-3.115-0.742-4.529,0l-45.17,23.738c-1.622,0.852-3.613,0.723-5.105-0.369 c-1.492-1.091-2.246-2.938-1.941-4.75l8.621-50.293c0.271-1.572-0.259-3.18-1.397-4.302l-36.52-35.62 c-1.331-1.302-1.797-3.227-1.236-4.977c0.58-1.768,2.104-3.05,3.918-3.307l50.516-7.337c1.574-0.225,2.939-1.219,3.645-2.664 l22.587-45.749c0.819-1.654,2.505-2.715,4.35-2.715c1.846,0,3.529,1.061,4.349,2.715l22.587,45.749 c0.705,1.445,2.069,2.439,3.645,2.664l50.516,7.337c1.813,0.257,3.34,1.539,3.918,3.307 C328.764,327.873,328.296,329.798,326.966,331.1z"/></g></svg></span>`;
}

/**
 * Compute crowns for an item
 * Returns max 1 crown: lastWeek only
 * @param {Object} item - The item with ranks data
 * @returns {Array<{type: "lastWeek", market: "paid" | "free"}>}
 */
export function computeCrowns(item) {
  return Array.isArray(item?.badges?.crowns) ? item.badges.crowns : [];
}

/**
 * Compute medals for an item (templates only)
 * Returns medals for category ranks 1, 2, or 3
 * @param {Object} item - The item with ranks data
 * @returns {Array<{icon: string, rank: number, category: string}>}
 */
export function computeMedals(item) {
  const medals = Array.isArray(item?.badges?.medals) ? item.badges.medals : [];
  return medals.map((entry) => ({
    icon: entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : "🥉",
    rank: entry.rank,
    category: entry.categoryId,
    categoryLabel: getCategoryLabel(entry.categoryId),
    period: entry.period,
    market: entry.market,
  }));
}

/**
 * Get medal score (gold=3, silver=2, bronze=1)
 * @param {Array<{icon: string, rank: number, category: string}>} medals
 * @returns {number}
 */
export function getMedalScore(medals) {
  return (Array.isArray(medals) ? medals : []).reduce((score, medal) => {
    if (medal.rank === 1) return score + 3;
    if (medal.rank === 2) return score + 2;
    if (medal.rank === 3) return score + 1;
    return score;
  }, 0);
}

/**
 * Format a change value for display
 * @param {number|string|null} change - The change value (number, "new", "moved-in", "comeback", "price_change", or null)
 * @param {string} context - The context for price_change labeling ("free" or "paid")
 * @returns {{text: string, colorClass: string, title: string}}
 */
function formatChange(change, context = "all") {
  if (change === "new") {
    return { text: "new", colorClass: "text-lime-400", title: "New to the marketplace" };
  }
  if (change === "moved-in") {
    return { text: "moved-in", colorClass: "text-cyan-400", title: "Moved into this category" };
  }
  if (change === "comeback") {
    return { text: "comeback", colorClass: "text-amber-500", title: "Was previously unlisted, now back" };
  }
  if (change === "price_change") {
    const title =
      context === "paid" ? "Was free, now paid" : context === "free" ? "Was paid, now free" : "Price changed";
    return { text: "repriced", colorClass: "text-purple-400", title };
  }
  if (change === null || change === undefined) {
    return { text: "-", colorClass: "text-muted-foreground", title: "" };
  }
  if (typeof change === "number") {
    if (change > 0) {
      return {
        text:
          '<span class="delta-marker delta-up" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M5 1 L9 8 L1 8 Z" fill="currentColor"/></svg></span>' +
          String(change),
        colorClass: "text-green-600",
        title: "",
      };
    }
    if (change < 0) {
      return {
        text:
          '<span class="delta-marker delta-down" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M5 1 L9 8 L1 8 Z" fill="currentColor"/></svg></span>' +
          String(Math.abs(change)),
        colorClass: "text-red-600",
        title: "",
      };
    }
    return { text: "0", colorClass: "text-muted-foreground", title: "" };
  }
  return { text: "-", colorClass: "text-muted-foreground", title: "" };
}

function formatCreatorDelta(deltaValue) {
  if (deltaValue === null || deltaValue === undefined) {
    return '<span class="text-muted-foreground">-</span>';
  }
  const delta = Number(deltaValue);
  if (!Number.isFinite(delta)) {
    return '<span class="text-muted-foreground">-</span>';
  }
  if (delta > 0) {
    return `<span class="text-green-600">+${delta.toLocaleString()}</span>`;
  }
  if (delta < 0) {
    return `<span class="text-red-600">${delta.toLocaleString()}</span>`;
  }
  return '<span class="text-muted-foreground">0</span>';
}

/**
 * Format a rank value for display
 * @param {number|null} rank - The rank value
 * @returns {string}
 */
function formatRank(rank) {
  if (rank === null || rank === undefined) {
    return "-";
  }
  return `#${rank}`;
}

function parsePriceToNumber(priceText) {
  if (typeof priceText !== "string") return null;
  const trimmed = priceText.trim();
  if (/^free$/i.test(trimmed)) return 0;
  const match = trimmed.match(/\$(\d+)/);
  if (!match) return null;
  return Number.parseInt(match[1], 10);
}

function resolvePreviousPriceNumber(currentPrice, previousPrice) {
  const explicitPreviousNumeric =
    typeof previousPrice === "number" ? previousPrice : parsePriceToNumber(previousPrice || "");
  if (Number.isFinite(explicitPreviousNumeric)) {
    const currentNumeric = parsePriceToNumber(currentPrice);
    if (Number.isFinite(currentNumeric) && currentNumeric === explicitPreviousNumeric) return null;
    return explicitPreviousNumeric;
  }
  return null;
}

function formatInlinePreviousPrice(currentPrice, previousPrice) {
  const previousNumeric = resolvePreviousPriceNumber(currentPrice, previousPrice);
  if (previousNumeric === null) return "";

  const previousLabel = previousNumeric <= 0 ? "Free" : `$${Math.round(previousNumeric)}`;
  return `<span class="price-previous with-tooltip"><span class="price-prev-arrow" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M6 1 L1 5 L6 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 5 H9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span>${previousLabel}<div class="tooltip top"><div class="tooltip-item">Price change since yesterday</div></div></span>`;
}

function renderChangeSpan(contextClass, changeInfo) {
  if (!changeInfo.title) {
    return `<span class="${contextClass} ${changeInfo.colorClass}">${changeInfo.text}</span>`;
  }
  return `<span class="${contextClass} ${changeInfo.colorClass} with-tooltip"><span class="change-text">${changeInfo.text}</span><div class="tooltip top"><div class="tooltip-item">${changeInfo.title}</div></div></span>`;
}

function renderRankWithBadge(icon, rankText, badgeClass = "") {
  const classSuffix = badgeClass ? ` ${badgeClass}` : "";
  return `<span class="rank-with-badge"><span class="rank-badge${classSuffix}">${icon}</span><span class="rank-number">${rankText}</span></span>`;
}

function formatStandardRank(contextKey, rank, crownedContexts) {
  const display = formatRank(rank);
  if (display === "-") return display;

  // Do not show crown in mixed-price contexts.
  if (contextKey === "alltime-all" || contextKey === "lastWeek-all") {
    return display;
  }

  if (rank === 1 && crownedContexts.has(contextKey)) {
    const rankCrownSvg = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.8306 3.443C12.6449 3.16613 12.3334 3 12.0001 3C11.6667 3 11.3553 3.16613 11.1696 3.443L7.38953 9.07917L2.74781 3.85213C2.44865 3.51525 1.96117 3.42002 1.55723 3.61953C1.15329 3.81904 0.932635 4.26404 1.01833 4.70634L3.70454 18.5706C3.97784 19.9812 5.21293 21 6.64977 21H17.3504C18.7872 21 20.0223 19.9812 20.2956 18.5706L22.9818 4.70634C23.0675 4.26404 22.8469 3.81904 22.4429 3.61953C22.039 3.42002 21.5515 3.51525 21.2523 3.85213L16.6106 9.07917L12.8306 3.443Z" fill="currentColor"/></svg>`;
    return renderRankWithBadge(rankCrownSvg, display, "rank-crown-badge");
  }

  return display;
}

function formatCategoryRank(contextKey, rank, itemType = "template") {
  if (rank === null || rank === undefined) return "-";

  // Category medals in rank cell are only for lastweek free/paid contexts.
  const isMedalContext = (itemType === "template" || itemType === "component") && /-lastweek-(free|paid)$/.test(
    contextKey,
  );
  if (isMedalContext) {
    if (rank === 1) return renderRankWithBadge(renderMedalSvg(1, 17, "rank-medal-svg"), `#${rank}`);
    if (rank === 2) return renderRankWithBadge(renderMedalSvg(2, 17, "rank-medal-svg"), `#${rank}`);
    if (rank === 3) return renderRankWithBadge(renderMedalSvg(3, 17, "rank-medal-svg"), `#${rank}`);
  }

  return `#${rank}`;
}

/**
 * Render a sparkline HTML string for 7-day trend data
 *
 * Color logic:
 * - Green (#22c55e): Improving trend (rank going down = better)
 * - Red (#ef4444): Declining trend (rank going up = worse)
 * - Gray (#9ca3af): Stable trend (ranks not changing significantly)
 *
 * @param {number[]|undefined} trendData - Array of 7 rank values (oldest to newest)
 * @param {Object} options - Optional configuration
 * @param {number} options.width - SVG width in pixels (default: 80)
 * @param {number} options.height - SVG height in pixels (default: 24)
 * @param {number} options.strokeWidth - Line stroke width (default: 2)
 * @returns {string} HTML string with sparkline span
 */
export function renderSparkline(trendData, options = {}) {
  // Handle missing or invalid data
  if (!trendData || !Array.isArray(trendData) || trendData.length === 0) {
    return `<span class="sparkline-placeholder text-muted-foreground"></span>`;
  }

  const slots = 7;
  const normalized = trendData.map((v) => {
    if (v === null || v === undefined) return null;
    const num = Number(v);
    return Number.isFinite(num) ? num : null;
  });
  const slotValues =
    normalized.length >= slots
      ? normalized.slice(normalized.length - slots)
      : Array.from({ length: slots - normalized.length }, () => null).concat(normalized);

  const values = slotValues.filter((v) => v !== null);

  // Need at least 2 points for a line chart
  if (values.length < 2) {
    return `<span class="sparkline-placeholder text-muted-foreground"></span>`;
  }

  // Configuration
  const width = options.width ?? 80;
  const height = options.height ?? 24;
  const strokeWidth = options.strokeWidth ?? 2;
  const padX = 2;
  const padTop = 2;
  const padBottom = 2;
  const plotW = Math.max(1, width - padX * 2);
  const plotH = Math.max(1, height - padTop - padBottom);
  const minRank = Math.min(...values);
  const maxRank = Math.max(...values);
  const range = Math.max(1, maxRank - minRank);
  const stepX = slots > 1 ? plotW / (slots - 1) : 0;
  const points = slotValues.map((rank, slotIndex) => {
    if (rank === null) return null;
    const x = padX + stepX * slotIndex;
    // Lower rank is better and should appear higher on chart.
    const normalized = (rank - minRank) / range;
    const y = padTop + normalized * plotH;
    return { x, y, rank };
  });
  const nonNullPoints = points.filter((p) => p !== null);

  const firstRank = values[0];
  const lastRank = values[values.length - 1];
  const delta = firstRank - lastRank;
  const stepColor = (prevRank, currRank) => {
    // Lower rank number is better: 12 -> 10 is an upward move (green).
    if (currRank < prevRank) return "#16a34a";
    if (currRank > prevRank) return "#dc2626";
    return "#9ca3af";
  };
  const segments = points
    .map((point, i) => {
      if (i === 0 || point === null) return "";
      const prev = points[i - 1];
      if (prev === null) return "";
      const color = stepColor(prev.rank, point.rank);
      return `<line x1="${prev.x.toFixed(2)}" y1="${prev.y.toFixed(2)}" x2="${point.x.toFixed(2)}" y2="${point.y.toFixed(2)}" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round"></line>`;
    })
    .join("");
  const circles = points
    .map((p, i) => {
      if (p === null) return "";
      const prev = i > 0 ? points[i - 1] : null;
      const next = i < points.length - 1 ? points[i + 1] : null;
      const color =
        prev !== null ? stepColor(prev.rank, p.rank) : next !== null ? stepColor(p.rank, next.rank) : "#9ca3af";
      return `<circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="1.5" fill="${color}" opacity="0.9"></circle>`;
    })
    .join("");
  const labelMode = shouldRenderSparklinePointLabels();
  const labelPoints = labelMode === "all" ? nonNullPoints : [];
  const pointLabels = labelPoints
    .map((p, i) => {
      const nonNullIndex = labelMode === "all" ? i : nonNullPoints.length - 1;
      const labelY = p.y <= 8 ? p.y + 7 : p.y - 4;
      const color =
        nonNullIndex === 0
          ? nonNullPoints[1]
            ? stepColor(nonNullPoints[0].rank, nonNullPoints[1].rank)
            : "#9ca3af"
          : stepColor(nonNullPoints[nonNullIndex - 1].rank, nonNullPoints[nonNullIndex].rank);
      return `<text x="${p.x.toFixed(2)}" y="${labelY.toFixed(2)}" text-anchor="middle" font-size="6" fill="${color}" opacity="0.95">${p.rank}</text>`;
    })
    .join("");

  const titleAttr = shouldRenderSparklineTitleAttr()
    ? ` title="7-day trend: ${firstRank} → ${lastRank} (${delta > 0 ? "+" : ""}${delta})"`
    : "";

  return `<span class="sparkline"${titleAttr}>
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" aria-hidden="true" focusable="false">
      ${segments}
      ${circles}
      ${pointLabels}
    </svg>
  </span>`;
}

/**
 * Render a single table row with ALL context spans
 *
 * New unified format:
 * - item.ranks = { all: [rank, change], free: [rank, change], paid: [rank, change], categoryId: [rank, change], ... }
 * - item.categories = ["categoryId1", "categoryId2", ...]
 * - item.type = "template" | "component"
 *
 * @param {Object} item - The unified item from ranks-data.json
 * @param {number} index - The item's index in the data array
 * @returns {string} The HTML string for the row
 */
export function renderRowWithAllSpans(item, index) {
  if (item.type === "creator") {
    const creatorUrl =
      item.creatorUrl && typeof item.creatorUrl === "string" && item.creatorUrl.length > 0
        ? item.creatorUrl
        : item.creatorSlug
          ? `https://framer.com/@${item.creatorSlug}`
          : "https://framer.com/marketplace/";
    const escapedName = escapeHtml(item.name || "Unknown creator");
    const creatorStats = item.creatorStats || {};
    const totalTemplates = Number(creatorStats.totalTemplates || 0);
    const totalTemplatesDelta1d =
      creatorStats.totalTemplatesDelta1d === null || creatorStats.totalTemplatesDelta1d === undefined
        ? null
        : Number(creatorStats.totalTemplatesDelta1d);
    const totalComponents = Number(creatorStats.totalComponents || 0);
    const totalComponentsDelta1d =
      creatorStats.totalComponentsDelta1d === null || creatorStats.totalComponentsDelta1d === undefined
        ? null
        : Number(creatorStats.totalComponentsDelta1d);
    const totalItems = Number(creatorStats.totalItems || 0);
    const totalItemsDelta1d =
      creatorStats.totalItemsDelta1d === null || creatorStats.totalItemsDelta1d === undefined
        ? null
        : Number(creatorStats.totalItemsDelta1d);
    const avatarHtml = `<img src="${escapeHtml(normalizeCreatorAvatarUrl(item.thumbnail))}" alt="" class="creator-avatar" loading="lazy" decoding="async" />`;
    const creatorColumnValues = {
      name: `<span class="creator-avatar-slot">${avatarHtml}</span><a href="${escapeHtml(creatorUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline">${escapedName}</a>`,
      templates: totalTemplates.toLocaleString(),
      templatesdelta: formatCreatorDelta(totalTemplatesDelta1d),
      components: totalComponents.toLocaleString(),
      componentsdelta: formatCreatorDelta(totalComponentsDelta1d),
      total: totalItems.toLocaleString(),
      totaldelta: formatCreatorDelta(totalItemsDelta1d),
    };

    const creatorCells = getColumnsForType("creator")
      .map((column) => {
        const cellValue = creatorColumnValues[column.id] ?? "-";
        return `<td class="${column.cellClass} font-medium text-xs">${cellValue}</td>`;
      })
      .join("");

    return `<tr data-idx="${index}" data-id="${item.id}" data-type="creator" class="hover:bg-muted/50">${creatorCells}</tr>`;
  }

  const isCategoryViewTrendEnabled = true;
  const crowns = computeCrowns(item);
  const crownedContexts = new Set(
    crowns
      .map((crown) => `${crown.period}-${crown.market}`)
      .filter((key) => key === "lastWeek-free" || key === "lastWeek-paid"),
  );

  // Build rank spans for all contexts
  const rankSpans = [];
  const changeSpans = [];
  const trendSpans = [];

  // All 6 standard contexts: alltime-all/free/paid + lastWeek-all/free/paid
  const standardContexts = [
    { key: "alltime-all", label: "all" },
    { key: "alltime-free", label: "free" },
    { key: "alltime-paid", label: "paid" },
    { key: "lastWeek-all", label: "all" },
    { key: "lastWeek-free", label: "free" },
    { key: "lastWeek-paid", label: "paid" },
  ];

  for (const { key, label } of standardContexts) {
    const rankEntry = item.ranks[key] || [null, null];
    const [rank, change] = rankEntry;

    const rankDisplay = formatStandardRank(key, rank, crownedContexts);
    const changeInfo = formatChange(change, label);

    rankSpans.push(`<span class="r-${key}">${rankDisplay}</span>`);
    changeSpans.push(renderChangeSpan(`c-${key}`, changeInfo));

    // Get trend data for this exact context only (no cross-context fallback).
    const trendData = item.trend?.[key];
    const trendHtml = renderSparkline(trendData, { width: 80, height: 24, strokeWidth: 2 });
    trendSpans.push(`<span class="t-${key}">${trendHtml}</span>`);
  }

  // Category contexts (templates only)
  for (const categoryId of item.categories || []) {
    const contexts = [
      `${categoryId}-alltime-all`,
      `${categoryId}-alltime-free`,
      `${categoryId}-alltime-paid`,
      `${categoryId}-lastweek-all`,
      `${categoryId}-lastweek-free`,
      `${categoryId}-lastweek-paid`,
    ];
    for (const contextKey of contexts) {
      const rankEntry = item.ranks[contextKey] || [null, null];
      const [rank, change] = rankEntry;

      const rankDisplay = formatCategoryRank(contextKey, rank, item.type);
      const label = contextKey.endsWith("-free") ? "free" : contextKey.endsWith("-paid") ? "paid" : "all";
      const changeInfo = formatChange(change, label);

      rankSpans.push(`<span class="r-${contextKey}">${rankDisplay}</span>`);
      changeSpans.push(renderChangeSpan(`c-${contextKey}`, changeInfo));

      // Category trend data is keyed by full category context.
      const trendData = isCategoryViewTrendEnabled ? item.trend?.[contextKey] : undefined;
      const trendHtml = renderSparkline(trendData, { width: 80, height: 24, strokeWidth: 2 });
      trendSpans.push(`<span class="t-${contextKey}">${trendHtml}</span>`);
    }
  }

  // Get image URL (prefer small thumbnail)
  let imageUrl = null;
  if (item.allImages) {
    imageUrl = item.allImages["64w"] || item.allImages["48w"] || item.allImages["32w"] || null;
  }
  if (!imageUrl) {
    imageUrl = item.thumbnail || null;
  }

  const thumbnail = imageUrl
    ? `<img src="${escapeHtml(imageUrl)}" alt="" class="thumbnail-image" loading="lazy" decoding="async" />`
    : '<div class="thumbnail-placeholder">No image</div>';

  // Format URLs
  const fullUrl = item.url.startsWith("http") ? item.url : `https://www.framer.com${item.url}`;
  const escapedName = escapeHtml(item.name);
  const escapedPrice = escapeHtml(item.price);
  const inlinePreviousPrice = formatInlinePreviousPrice(item.price, item.previousPrice);

  // Determine price type for filtering
  const priceType = item.price === "Free" ? "Free" : "Paid";

  // Format author URL
  let authorLink = "";
  if (item.authorName && item.authorUrl && typeof item.authorUrl === "string" && item.authorUrl.length > 0) {
    let authorUrl;
    if (item.authorUrl.startsWith("http")) {
      authorUrl = item.authorUrl;
    } else if (item.authorUrl.startsWith("/creators/")) {
      authorUrl = `https://framer.com/@${item.authorUrl.replace("/creators/", "").replace(/\/$/, "")}`;
    } else {
      authorUrl = `https://www.framer.com${item.authorUrl}`;
    }
    const escapedAuthorName = escapeHtml(String(item.authorName));
    authorLink = `<a href="${escapeHtml(authorUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline">${escapedAuthorName}</a>`;
  }

  const CROWN_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.8306 3.443C12.6449 3.16613 12.3334 3 12.0001 3C11.6667 3 11.3553 3.16613 11.1696 3.443L7.38953 9.07917L2.74781 3.85213C2.44865 3.51525 1.96117 3.42002 1.55723 3.61953C1.15329 3.81904 0.932635 4.26404 1.01833 4.70634L3.70454 18.5706C3.97784 19.9812 5.21293 21 6.64977 21H17.3504C18.7872 21 20.0223 19.9812 20.2956 18.5706L22.9818 4.70634C23.0675 4.26404 22.8469 3.81904 22.4429 3.61953C22.039 3.42002 21.5515 3.51525 21.2523 3.85213L16.6106 9.07917L12.8306 3.443Z" fill="currentColor"/></svg>`;

  const crownIcons = crowns
    .map((crown) => {
      const periodLabel = crown.period === "alltime" ? "All Time" : "Last Week";
      const marketLabel = crown.market === "paid" ? "Paid" : "Free";
      const itemTypeLabel = item.type === "component" ? "Components" : "Templates";
      const tooltipItem = `#1 in ${periodLabel} ${marketLabel} ${itemTypeLabel}`;
      return `<span class="with-tooltip crown-with-tooltip"><span class="crown-icon">${CROWN_SVG}</span><div class="tooltip left"><button type="button" class="tooltip-item crown-jump-btn" data-crown-period="${crown.period}" data-crown-market="${crown.market}" data-item-type="${item.type}">${tooltipItem}</button></div></span>`;
    })
    .join("");

  const medals = computeMedals(item);
  let medalsContent = "";
  if (medals.length > 0) {
    const medalCounts = { 1: 0, 2: 0, 3: 0 };
    for (const m of medals) {
      if (m.rank === 1 || m.rank === 2 || m.rank === 3) {
        medalCounts[m.rank] += 1;
      }
    }

    const medalsHtml = `
      <span class="medals-summary">
        <span class="medal-slot${medalCounts[1] === 0 ? " is-empty" : ""}">
          <span class="medal-icon">${renderMedalSvg(1, 13, "summary-medal-svg")}</span><span class="medal-count">${medalCounts[1]}</span>
        </span>
        <span class="medal-slot${medalCounts[2] === 0 ? " is-empty" : ""}">
          <span class="medal-icon">${renderMedalSvg(2, 13, "summary-medal-svg")}</span><span class="medal-count">${medalCounts[2]}</span>
        </span>
        <span class="medal-slot${medalCounts[3] === 0 ? " is-empty" : ""}">
          <span class="medal-icon">${renderMedalSvg(3, 13, "summary-medal-svg")}</span><span class="medal-count">${medalCounts[3]}</span>
        </span>
      </span>`;
    const itemTypeLabel = item.type === "component" ? "Components" : "Templates";
    const marketSet = new Set(medals.map((m) => m.market));
    const marketLabel =
      marketSet.size === 1
        ? marketSet.has("paid")
          ? `Paid ${itemTypeLabel}`
          : `Free ${itemTypeLabel}`
        : `Free/Paid ${itemTypeLabel}`;
    const tooltipTitle = `<div class="tooltip-title">Category Medals<br><span class="tooltip-title-sub">Last Week</span><br><span class="tooltip-title-sub">${marketLabel}</span></div>`;
    const tooltipItems = medals
      .map((m) => {
        return `<button type="button" class="tooltip-item medal-jump-btn" data-category-id="${escapeHtml(m.category)}" data-rank="${m.rank}" data-market="${m.market}" data-item-type="${item.type}">${renderMedalSvg(m.rank, 12, "tooltip-medal-svg")} #${m.rank} in ${escapeHtml(m.categoryLabel)}</button>`;
      })
      .join("");
    medalsContent = `<div class="with-tooltip">${medalsHtml}<div class="tooltip right">${tooltipTitle}${tooltipItems}</div></div>`;
  } else {
    medalsContent = "";
  }

  // Build categories data attribute for filtering
  const categoriesAttr = item.categories?.length > 0 ? ` data-cats="${item.categories.join(",")}"` : "";

  const standardColumnValues = {
    rank: rankSpans.join(""),
    change: changeSpans.join(""),
    trend: trendSpans.join(""),
    price: `<span class="price-content">${escapedPrice}${inlinePreviousPrice}</span>`,
    image: thumbnail,
    name: `<div class="crown-wrapper"><a href="${escapeHtml(fullUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline">${escapedName}</a>${crownIcons}</div>`,
    medals: medalsContent,
    author: authorLink,
  };

  const standardCells = getColumnsForType(item.type)
    .map((column) => {
      if (column.id === "rank") {
        return `<td class="${column.cellClass} rank-cell text-center font-medium text-xs tabular-nums">${standardColumnValues.rank}</td>`;
      }
      if (column.id === "change") {
        return `<td class="${column.cellClass} change-cell text-center font-medium text-xs">${standardColumnValues.change}</td>`;
      }
      if (column.id === "trend") {
        return `<td class="${column.cellClass} trend-cell text-center font-medium text-xs">${standardColumnValues.trend}</td>`;
      }
      if (column.id === "price") {
        return `<td class="${column.cellClass} text-center font-medium text-xs">${standardColumnValues.price}</td>`;
      }
      if (column.id === "image") {
        return `<td class="${column.cellClass} text-center p-1">${standardColumnValues.image}</td>`;
      }
      if (column.id === "name") {
        return `<td class="${column.cellClass} font-medium text-xs text-left">${standardColumnValues.name}</td>`;
      }
      if (column.id === "medals") {
        return `<td class="${column.cellClass} medals-cell">${standardColumnValues.medals}</td>`;
      }
      if (column.id === "author") {
        return `<td class="${column.cellClass} font-medium text-xs text-left">${standardColumnValues.author}</td>`;
      }
      return `<td class="${column.cellClass}">-</td>`;
    })
    .join("");

  return `<tr data-idx="${index}" data-id="${item.id}" data-type="${item.type}" data-price="${priceType}"${categoriesAttr} class="hover:bg-muted/50">${standardCells}</tr>`;
}

/**
 * Render all rows at once (called once at page load)
 * @param {Array} items - All items from ranks-data.json
 * @returns {Array<{html: string, item: Object, index: number}>} Array of row objects
 */
export function renderAllRows(items) {
  const startTime = performance.now();

  log(`[RENDER] Rendering ${items.length} rows...`);

  const rows = items.map((item, index) => ({
    html: renderRowWithAllSpans(item, index),
    item,
    index,
  }));

  const duration = Math.round(performance.now() - startTime);
  log(`[RENDER] Rendered ${rows.length} rows in ${duration}ms`);

  return rows;
}

// Legacy exports for backward compatibility during migration
export { escapeHtml };
